// taken from the (now unmaintained) lib that was used in previous apps with apollo-client
// https://github.com/with-heart/apollo-link-response-resolver

// keeping it as JS because it would be hard to type (with a lot of unknowns anyway)
// and this is more readable and it shouldn’t change much

// * HELPERS

// whether the value is defined (not null/undefined)
const isDefined = (value) => value !== null && value !== undefined
// whether the value is an object
const isObject = (value) => isDefined(value) && !Array.isArray(value) && typeof value === 'object'
// whether the value is an array
const isArray = (value) => isDefined(value) && Array.isArray(value)

// whether the object is in the fields that should be transformed
//  - it’s decided by comparing the `__typename` prop that is loaded on GQL types/objects
//    with the TOP LEVEL keys of the fields-to-be-transformed map
const isMarkedForTransform = (dataObject, typeNameMap) => {
  return dataObject.__typename && Object.keys(typeNameMap).includes(dataObject.__typename)
}

// for arrays, resolve each of its items one by one (to go deeper)
const mapArray = (dataArray, typeNameMap, transformer) => {
  return dataArray.map((item) => mapOperationResult(transformer)(typeNameMap)(item))
}

// for objects, find out if the object itself has any properties that should be transformed
//  - if yes, transform it
//  - if not, resolve each of its properties one by one
const mapObject = (dataObject, typeNameMap, transformer) => {
  const typeMarked = isMarkedForTransform(dataObject, typeNameMap)

  // the object’s type name is listed as a TOP LEVEL key in the fields that should be transformed
  // pass it to the transformation function
  if (typeMarked) {
    return transformObject(dataObject, typeNameMap, transformer)
  }

  // the object doesn’t have any property that should be transformed
  // try it’s properties one by one (to go deeper)
  return Object.entries(dataObject).reduce((acc, [key, value]) => {
    acc[key] = mapOperationResult(transformer)(typeNameMap)(value)

    return acc
  }, {})
}

// transform keys of an object of a type that was marked for transformation
const transformObject = (dataObject, typeNameMap, transformer) => {
  // get the object of transformation keys for this type (by the type name)
  const typeMap = typeNameMap[dataObject.__typename]

  // for each of the data object keys decide if it should be transformed
  return Object.entries(dataObject).reduce((acc, [key, value]) => {
    const propertyMarked = typeMap[key] !== undefined

    // if the key of the data object’s property
    // is also in the keys of the object assigned to the same type name in the `typeNameMap`
    // call the transformer function for that value & store the result
    // otherwise resolve the property (to go deeper)
    if (propertyMarked) {
      acc[key] = transformer(value)
    } else {
      acc[key] = mapOperationResult(transformer)(typeNameMap)(value)
    }

    return acc
  }, {})
}

/**
 * the creator function for mapping over a GQL response object
 * and calling transformation function for those properties that are defined in the typeNameMap object
 * the comparison is done using the `__typename` property that most of the GQL objects should have
 * and will not change on our FE GQL operation field renames (the properties of those type might though!)
 *
 * it is a curried function with this API:
 *  - 1st (): `transformer` function
 *    - is called for properties that should be transformed
 *    - get the original value as 1st param and should return the transformed value that will be used instead
 *  - 2nd (): `typeNameMap` object
 *    -  an object where TOP LEVEL keys should correspond to the `__typename` values of those object/GQL types
 *        that has some properties that should be transformed
 *        and as a value of those TOP LEVEL keys should be again an object where key will be those properties
 *        that should be transformed on that type
 *    - // ! the property names (keys of the second-level objects) has to be kept in sync with those properties in GQL files (don’t forget to rename them as well)
 *        (the top-level keys – type names – will not change unless BE changes the schema)
 *    - the "leaf" values of this object does not matter, but it’s typed to require null (not used really)
 *  - 3rd (): `data` object
 *    - on initial call (outside of this file) it should be the GQL operation result (what has returned from BE)
 *    - locally in this file it’s also called with subparts of that initial object (using recursion, yea) – hence the data name
 *
 * example of usage (initialize serialized ISO dates into full JS Date objects):
 *  - transformer – function transforming a ISO date string into a Date object
 *    - (isoDate: Nullable<DateString>) => (isoDate !== null ? new Date(isoDate) : null)
 *  - typeNameMap – object with `__typename`s of all types, that have some property(ies) of type Date & the keys of those date properties
 *    - {
 *        Order: {
 *          updateTimestamp: null
 *        },
 *        CompanyBranch: {
 *          willOpenAt: null
 *          willCloseAt: null
 *        },
 *        BusinessHoursInterval: {
 *          startsAt: null,
 *          endsAt: null,
 *        }
 *        ...
 *      }
 */
export const mapOperationResult = (transformer) => (typeNameMap) => (data) => {
  if (isArray(data)) {
    return mapArray(data, typeNameMap, transformer)
  }

  if (isObject(data)) {
    return mapObject(data, typeNameMap, transformer)
  }

  return data
}
